import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { Box, FormControl, InputLabel, useMediaQuery, useTheme } from '@material-ui/core';

import { groupedOptions as catOptions } from '../../constants/breeds-cat';
import { groupedOptions as dogOptions } from '../../constants/breeds-dog';
import { StepWizardChildProps } from '../StepWizard';
import Question from './components/Question';
import Select from './components/Select';
import Step from './components/Step';
import TextField from './components/TextField';
import ToggleButton from './components/ToggleButton';
import ToggleButtonGroup from './components/ToggleButtonGroup';

interface FormInputs {
  name: string;
  gender: 'M' | 'F';
  age: number;
  breed: string;
}

interface Props extends Partial<StepWizardChildProps> {
  data?: any;
  onUpdate?: (data: any) => void;
  onValidate?: (valid: boolean) => void;
}

const step = 2;

const getAgeOptions = (maxAgeYear: number) => {
  const ageOptions = [
    {
      label: `0 - 7 weeks`,
      value: 0,
    },
    {
      label: `8 weeks - 11 months`,
      value: 0.5,
    },
    {
      label: `1 year`,
      value: 1,
    },
  ];

  Array.from(Array(maxAgeYear - 1).keys()).forEach((i) =>
    ageOptions.push({ label: `${i + 2} years`, value: i + 2 }),
  );

  return ageOptions;
};

const StepAboutYourPet = ({ data, onUpdate, onValidate }: Props) => {
  const { register, formState, getValues, errors, control } = useForm<
    FormInputs
  >({
    mode: 'onChange',
  });

  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('sm'));

  useEffect(() => {
    if (typeof onValidate === 'function') {
      onValidate(formState.isValid);
    }

    if (typeof onUpdate === 'function') {
      onUpdate(getValues());
    }
  }, [formState.isValid]);

  return (
    <form>
      <Box>
        <Step num={step} />
        <Question label="Tell us more about your pet?" />
        <Box display="flex" flexDirection="column" alignItems="center">
          <TextField
            inputRef={register({
              required: { value: true, message: 'Required' },
            })}
            required
            label="Pet's Name"
            name="name"
            helperText={errors.name && errors.name.message}
            style={{ marginBottom: 16 }}
          />

          <Controller
            control={control}
            name="gender"
            rules={{ required: true }}
            render={(props) => (
              <FormControl style={{ width: '100%', maxWidth: 468 }}>
                <InputLabel shrink>Pet's Gender</InputLabel>
                <ToggleButtonGroup
                  value={props.value}
                  exclusive
                  onChange={(e, value) => {
                    if (!!value) {
                      props.onChange(value);
                    }
                  }}
                  aria-label="male or female"
                  style={{ marginTop: 24, marginBottom: 16 }}
                >
                  <ToggleButton value="M" aria-label="Male">
                    Male
                  </ToggleButton>
                  <ToggleButton value="F" aria-label="Female">
                    Female
                  </ToggleButton>
                </ToggleButtonGroup>
              </FormControl>
            )}
          />

          <Controller
            control={control}
            name="age"
            rules={{ required: true }}
            render={(props) => {
              return (
                <FormControl
                  style={{ width: '100%', maxWidth: 468, marginBottom: 16 }}
                >
                  <InputLabel shrink>
                    Pet's Age (best guess works too 😊)
                  </InputLabel>
                  <Select
                    menuPlacement="auto"
                    placeholder={isDesktop ? 'Select Age' : 'Age'}
                    onChange={(
                      value: { value: string; label: string },
                      action,
                    ) => {
                      props.onChange(value.value);
                    }}
                    options={getAgeOptions(20)}
                  />
                </FormControl>
              );
            }}
          />

          <Controller
            control={control}
            name="breed"
            rules={{ required: true }}
            render={(props) => {
              return (
                <FormControl style={{ width: '100%', maxWidth: 468 }}>
                  <InputLabel shrink>Pet's Breed</InputLabel>
                  <Select
                    menuPlacement="auto"
                    placeholder={isDesktop ? 'Select Breed' : 'Breed'}
                    onChange={(
                      value: { value: string; label: string },
                      action,
                    ) => {
                      props.onChange(value.value);
                    }}
                    options={data.species === 'Dog' ? dogOptions : catOptions}
                  />
                </FormControl>
              );
            }}
          />
        </Box>
      </Box>
    </form>
  );
};

export default StepAboutYourPet;
